<template>
  <div class="container-fluid">
    <!-- Library Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="me-3" style="font-size: 48px;">📚</div>
              <div>
                <h3 class="mb-1 fw-bold" style="color: #9d174d;">Reading Library</h3>
                <p class="mb-0" style="color: #be185d;">Discover amazing stories and adventures!</p>
              </div>
            </div>
            <div class="d-flex gap-3">
              <button class="btn" style="background: #8b5cf6; color: white; border-radius: 20px;" @click="toggleSearch">
                🔍 Search Stories
              </button>
              <button class="btn" style="background: #f59e0b; color: white; border-radius: 20px;" @click="goFavorites">
                ⭐ My Favorites
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Bar -->
    <div v-if="showSearch" class="row mb-3">
      <div class="col-12">
        <div class="input-group">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Search stories by title, summary, author or category..."
            @keyup.enter="doSearch"
          />
          <button class="btn btn-primary" @click="doSearch">Search</button>
        </div>
      </div>
    </div>

    <!-- Continue Reading Section -->
    <div v-if="continueReadingBooks.length > 0" class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center">
              <div class="me-3" style="font-size: 32px;">📖</div>
              <div>
                <h5 class="mb-1 fw-bold" style="color: #92400e;">Continue Reading</h5>
                <p class="mb-0" style="color: #d97706;">Pick up where you left off</p>
              </div>
            </div>
            <div class="reading-streak" style="color: #92400e;">
              <i class="bi bi-fire"></i> {{ readingStreak }} day streak!
            </div>
          </div>
          <div class="row g-3">
            <div v-for="book in continueReadingBooks" :key="book.id" class="col-md-6">
              <div class="continue-reading-card" @click="readStory(book)">
                <div class="d-flex align-items-center">
                  <div class="book-thumbnail me-3">{{ book.thumbnail }}</div>
                  <div class="flex-grow-1">
                    <h6 class="fw-bold mb-1">{{ book.title }}</h6>
                    <p class="small text-muted mb-2">{{ book.author }}</p>
                    <div class="progress-container">
                      <div class="progress-bar-small">
                        <div class="progress-fill-small" :style="{ width: getReadingProgress(book) + '%' }"></div>
                      </div>
                      <small class="text-muted">{{ getReadingProgress(book) }}% complete</small>
                    </div>
                  </div>
                  <button class="btn btn-sm btn-warning">
                    Continue →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filter Tabs -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="nav-pills">
          <button
            v-for="filter in filters"
            :key="filter.key"
            class="nav-pill"
            :class="{ active: activeFilter === filter.key }"
            @click="activeFilter = filter.key"
          >
            <i :class="filter.icon"></i>
            {{ filter.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Featured Story -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <div class="d-flex align-items-center mb-3">
                <div class="me-3" style="font-size: 32px;">🌟</div>
                <div>
                  <h5 class="mb-1 fw-bold" style="color: #1e40af;">Featured Story</h5>
                  <p class="mb-0" style="color: #3b82f6;">Story of the Week</p>
                </div>
              </div>
              <h4 class="fw-bold mb-2" style="color: #1e40af;">{{ featuredStory.title }}</h4>
              <p class="mb-3" style="color: #3b82f6;">
                {{ featuredStory.description }}
              </p>
              <div class="d-flex align-items-center gap-3 mb-3">
                <span class="badge px-3 py-2" style="background: #10b981; color: white; border-radius: 20px;">
                  {{ featuredStory.readingLevel }}
                </span>
                <span class="badge px-3 py-2" style="background: #f59e0b; color: white; border-radius: 20px;">
                  {{ featuredStory.pages }} pages
                </span>
                <span class="badge px-3 py-2" style="background: #8b5cf6; color: white; border-radius: 20px;">
                  ⭐ {{ featuredStory.rating || '4.8' }} rating
                </span>
              </div>
              <button class="btn btn-lg" style="background: #ef4444; color: white; border-radius: 20px;" @click="readStory(featuredStory)">
                📖 Start Reading →
              </button>
            </div>
            <div class="col-lg-4 text-center">
              <div 
                class="story-featured-image"
                style="width: 200px; height: 200px; background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%); border-radius: 20px; display: flex; align-items: center; justify-content: center; font-size: 80px; margin: 0 auto;"
              >
                {{ featuredStory.thumbnail }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stories Grid -->
    <div class="row mb-4">
      <div class="col-12">
        <h5 class="mb-3 fw-bold" style="color: var(--text-primary);">
          {{ getFilterTitle() }}
        </h5>
      </div>
    </div>

    <div class="row g-4">
      <div 
        v-for="story in filteredStories" 
        :key="story.id" 
        class="col-lg-3 col-md-4 col-sm-6"
      >
        <div class="story-card" :data-progress="getStoryStatus(story)">
          <div class="story-card-image">
            {{ story.thumbnail }}
          </div>
          <div class="story-card-content">
            <h6 class="fw-bold mb-2">{{ story.title }}</h6>
            <p class="small text-muted mb-2">by {{ story.author }}</p>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <span class="story-level-badge">{{ story.readingLevel }}</span>
              <small class="text-muted">{{ story.pages }} pages</small>
            </div>
            <div v-if="getReadingProgress(story) > 0" class="reading-progress-indicator mb-2">
              <div class="progress-bar-small">
                <div class="progress-fill-small" :style="{ width: getReadingProgress(story) + '%' }"></div>
              </div>
              <small class="text-muted">{{ getReadingProgress(story) }}% complete</small>
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <span class="me-1">⭐</span>
                <small>{{ story.rating }}</small>
              </div>
              <button 
                class="btn btn-sm" 
                style="background: var(--role-primary); color: white; border-radius: 15px;"
                @click="readStory(story)"
              >
                {{ getReadingProgress(story) > 0 ? 'Continue' : 'Read' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More -->
    <div class="row mt-5">
      <div class="col-12 text-center">
        <button class="btn btn-lg" style="background: var(--role-gradient); color: white; border-radius: 25px; padding: 12px 40px;" @click="loadMore">
          📚 Load More Stories
        </button>
      </div>
    </div>

    <!-- Story Reader -->
    <StoryReader 
      v-if="showStoryReader && selectedStory"
      :book="selectedStory"
      :start-page="getLastReadPage(selectedStory)"
      @close="closeStoryReader"
      @find-similar="findSimilarBooks"
    />
  </div>
</template>

<script>
import StoryReader from './StoryReader.vue'
import { 
  getSuggestions,
  getMoreStories,
  getByLevel,
  getFavourites,
  getRecents,
  getBook,
  searchReadings
} from '../services/readingService.js'

export default {
  name: 'LibraryPage',
  components: {
    StoryReader
  },
  props: {
    stories: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeFilter: 'all',
      showStoryReader: false,
      selectedStory: null,
      loadedStories: [],
      loading: false,
      loadError: '',
      showSearch: false,
      searchQuery: '',
      filters: [
        { key: 'all', label: 'All Stories', icon: 'bi-grid' },
        { key: 'level1', label: 'Level 1', icon: 'bi-1-circle' },
        { key: 'level2', label: 'Level 2', icon: 'bi-2-circle' },
        { key: 'level3', label: 'Level 3', icon: 'bi-3-circle' },
        { key: 'favorites', label: 'Favorites', icon: 'bi-heart-fill' },
        { key: 'recent', label: 'Recently Read', icon: 'bi-clock' }
      ]
    }
  },
  async mounted() {
    await this.loadStories();
  },
  watch: {
    activeFilter() {
      this.loadStories()
    }
  },
  computed: {
    allStories() {
      return this.loadedStories.length > 0 ? this.loadedStories : this.stories;
    },
    
    filteredStories() {
      if (this.activeFilter === 'all') {
        return this.allStories
      }
      // For server-filtered lists (favorites, recent), the data already contains only those items
      if (this.activeFilter === 'favorites') {
        return this.allStories
      }
      if (this.activeFilter === 'recent') {
        return this.allStories
      }
      
      if (this.activeFilter.startsWith('level')) {
        const level = this.activeFilter.replace('level', 'Level ')
        return this.allStories.filter(story => story.readingLevel === level)
      }
      
      return this.allStories
    },
    
    continueReadingBooks() {
      const savedProgress = JSON.parse(localStorage.getItem('readingProgress') || '{}');
      return this.allStories.filter(story => {
        const progress = savedProgress[story.id];
        return progress && progress.currentPage > 0 && progress.currentPage < story.content.length - 1;
      }).slice(0, 4); // Show max 4 books
    },
    
    featuredStory() {
      // Return the first story or a default featured story
      if (this.allStories.length > 0) {
        return this.allStories[0];
      }
      
      // Return a complete featured story with content structure
      return {
        id: 'featured',
        title: 'The Emotion Detective Agency',
        author: 'Dr. Sarah Miller',
        description: 'Join Maya and her team as they solve mysteries using their emotional intelligence!',
        readingLevel: 'Level 3',
        pages: 7,
        rating: 4.8,
        thumbnail: '🕵️‍♀️',
        content: [
          {
            pageNumber: 1,
            title: 'Chapter 1: The Mystery Begins',
            text: 'Emma noticed something strange about her classmate Jake. He had been sitting alone during lunch for three days in a row, staring at his sandwich without taking a bite. His usual bright smile was nowhere to be seen, and he hadn\'t participated in any games during recess.',
            illustration: '🕵️‍♀️'
          },
          {
            pageNumber: 2,
            title: 'Chapter 2: Gathering Clues',
            text: 'Emma decided to become an emotion detective. She watched Jake carefully, looking for clues about how he was feeling. She noticed his shoulders were slumped, he avoided eye contact, and he seemed to be moving more slowly than usual.',
            illustration: '🔍'
          },
          {
            pageNumber: 3,
            title: 'Chapter 3: The Investigation',
            text: 'During art class, Emma sat next to Jake. "Hi Jake," she said gently. "I noticed you seem different lately. Are you okay?" Jake looked up, surprised that someone had noticed. His eyes looked a little watery.',
            illustration: '🎨'
          },
          {
            pageNumber: 4,
            title: 'Chapter 4: Solving the Mystery',
            text: '"My dog Buddy has been sick," Jake whispered. "The vet says he might not get better. I just feel so sad and worried." Emma felt her heart fill with understanding. Now the mystery was solved - Jake was feeling sad and worried about his beloved pet.',
            illustration: '🐕'
          },
          {
            pageNumber: 5,
            title: 'Chapter 5: Showing Empathy',
            text: 'Emma knew exactly what to do. She put her hand on Jake\'s shoulder and said, "I\'m so sorry about Buddy. That must be really hard. Would you like to tell me about him?" Jake\'s face brightened a little as he began to share stories about his furry friend.',
            illustration: '🤗'
          },
          {
            pageNumber: 6,
            title: 'Chapter 6: The Power of Understanding',
            text: 'Over the next few days, Emma continued to check on Jake. She listened when he wanted to talk about Buddy, and she sat with him during lunch. Other classmates noticed and began including Jake in their activities too.',
            illustration: '👫'
          },
          {
            pageNumber: 7,
            title: 'Chapter 7: A Happy Ending',
            text: 'A week later, Jake came to school with a big smile. "Buddy is getting better!" he announced. Emma smiled too, not just because of the good news, but because she had learned something important: being an emotion detective helped her be a better friend.',
            illustration: '🌟'
          }
        ]
      };
    },
    
    readingStreak() {
      const streak = JSON.parse(localStorage.getItem('readingStreak') || '{"current": 0}');
      return streak.current;
    }
  },
  methods: {
    getFilterTitle() {
      const filter = this.filters.find(f => f.key === this.activeFilter)
      return filter ? filter.label : 'All Stories'
    },
    
    getReadingProgress(book) {
      const savedProgress = JSON.parse(localStorage.getItem('readingProgress') || '{}');
      const progress = savedProgress[book.id];
      if (!progress) return 0;
      // Prefer content length if available; otherwise fall back to pages/page_cnt; avoid divide-by-zero
      const totalPages = (book && Array.isArray(book.content) && book.content.length > 0)
        ? book.content.length
        : (Number.isFinite(book.pages) && book.pages > 0)
          ? book.pages
          : (Number.isFinite(book.page_cnt) && book.page_cnt > 0)
            ? book.page_cnt
            : 1;
      const current = Math.max(0, (progress.currentPage ?? 0) + 1);
      return Math.min(100, Math.round((current / totalPages) * 100));
    },
    
    getLastReadPage(book) {
      const savedProgress = JSON.parse(localStorage.getItem('readingProgress') || '{}');
      const progress = savedProgress[book.id];
      return progress ? progress.currentPage : 0;
    },
    
    getStoryStatus(story) {
      const progress = this.getReadingProgress(story);
      if (progress === 0) return 'new';
      if (progress === 100) return 'completed';
      return 'reading';
    },
    
    async readStory(story) {
      try {
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        if (!userId) return
        // Ensure we have full content from backend
        if (!story.content || !Array.isArray(story.content) || story.content.length === 0) {
          const book = await getBook(userId, story.id)
          if (book && Array.isArray(book.content)) {
            story.content = book.content
          }
        }
        if (!story.content || !Array.isArray(story.content) || story.content.length === 0) {
          this.showFeedback('This story content is not available yet.', 'error')
          return
        }
        this.selectedStory = story
        this.showStoryReader = true
      } catch (e) {
        this.showFeedback('Failed to load story', 'error')
      }
    },
    
    closeStoryReader() {
      this.showStoryReader = false;
      this.selectedStory = null;
    },
    
    async loadStories() {
      try {
        this.loading = true
        this.loadError = ''
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        if (!userId) { this.loadedStories = []; return }

        if (this.activeFilter === 'all') {
          this.loadedStories = await getSuggestions(userId)
        } else if (this.activeFilter === 'favorites') {
          // When switching to a new filter, don't exclude items based on previous results
          this.loadedStories = await getFavourites(userId, [])
        } else if (this.activeFilter === 'recent') {
          // Avoid excluding the very item we just read by passing an empty loaded list
          this.loadedStories = await getRecents(userId, [])
        } else if (this.activeFilter.startsWith('level')) {
          const level = parseInt(this.activeFilter.replace('level',''))
          this.loadedStories = await getByLevel(userId, level, [])
        } else {
          // unknown filter falls back to suggestions
          this.loadedStories = await getSuggestions(userId)
        }
      } catch (error) {
        this.loadError = 'Error loading stories'
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      try {
        const user = JSON.parse(localStorage.getItem('currentUser') || 'null')
        const userId = user?.id
        const loadedIds = this.loadedStories.map(s => s.id)
        const more = await getMoreStories(userId, loadedIds)
        const seen = new Set(loadedIds)
        this.loadedStories = [...this.loadedStories, ...more.filter(s => !seen.has(s.id))]
      } catch (e) {
        this.showFeedback('Failed to load more stories', 'error')
      }
    },

    toggleSearch() {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      }
    },

    async doSearch() {
      try {
        const q = (this.searchQuery || '').trim()
        if (!q) return
        const raw = localStorage.getItem('currentUser')
        const user = raw ? JSON.parse(raw) : null
        const userId = user?.id
        if (!userId) return
        this.loading = true
        const results = await searchReadings(userId, q)
        this.loadedStories = results
        this.activeFilter = 'all'
      } catch (e) {
        this.showFeedback('Search failed', 'error')
      } finally {
        this.loading = false
      }
    },

    goFavorites() {
      this.activeFilter = 'favorites'
    },

    findSimilarBooks(book) {
      // Filter stories by same category or reading level
      this.activeFilter = book.category ? book.category.toLowerCase() : 'all';
      this.showFeedback(`Found ${this.filteredStories.length} similar books!`, 'success');
    },
    
    showFeedback(message, type) {
      const alertClass = type === 'success' ? 'alert-success' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 350px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      
      document.body.appendChild(alert);
      setTimeout(() => {
        if (alert.parentNode) {
          alert.parentNode.removeChild(alert);
        }
      }, 4000);
    }
  }
}
</script>

<style scoped>
.story-featured-image {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.nav-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.nav-pill {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 12px 20px;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
}

.nav-pill:hover {
  background: var(--bg-tertiary);
  border-color: var(--role-primary);
}

.nav-pill.active {
  background: var(--role-primary);
  color: white;
  border-color: var(--role-primary);
}

.story-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
}

.story-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.story-card-image {
  background: var(--role-light);
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  border-bottom: 1px solid var(--border-color);
}

.story-card-content {
  padding: 15px;
  color: var(--text-primary);
}

.story-card-content h6 {
  color: var(--text-primary);
}

.story-card-content .text-muted {
  color: var(--text-muted) !important;
}

.story-level-badge {
  background: var(--role-light);
  color: var(--role-primary);
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
}

/* Continue Reading Styles */
.continue-reading-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 15px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.continue-reading-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  border-color: var(--role-primary);
}

.book-thumbnail {
  width: 50px;
  height: 50px;
  background: var(--role-light);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.progress-bar-small {
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill-small {
  height: 100%;
  background: linear-gradient(90deg, var(--role-primary), var(--role-secondary));
  border-radius: 3px;
  transition: width 0.3s ease;
}

.reading-streak {
  font-weight: 600;
  font-size: 16px;
}

.reading-progress-indicator {
  margin-top: 10px;
}

/* Enhanced Story Cards */
.story-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, var(--role-primary), var(--role-secondary));
  border-radius: 17px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.story-card:hover::before {
  opacity: 0.1;
}

/* Reading Progress Indicators */
.story-card[data-progress="new"] {
  border-left: 4px solid var(--role-primary);
}

.story-card[data-progress="reading"] {
  border-left: 4px solid var(--role-warning);
}

.story-card[data-progress="completed"] {
  border-left: 4px solid var(--role-success);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .continue-reading-card {
    padding: 12px;
  }
  
  .book-thumbnail {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  
  .reading-streak {
    font-size: 14px;
  }
}
</style> 